import { memo } from 'react';
import type { ReactNode } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormLabel, Button, Typography } from '@mui/material';
import { useCommonStyles } from '../../styles/GenericStyle';
import ScanInput from '../ScanInput';

export type ScannerModalProps = {
    open: boolean;
    title: string;
    label: string;
    placeholder: string;
    value: string;
    error: string;
    showInput?: boolean;
    infoText?: string;
    mappedValueText?: string;
    primaryButtonText?: string;
    secondaryButtonText?: string;
    secondaryButtonStartIcon?: ReactNode;
    /** Incrementing this key remounts ScanInput, clearing and re-focusing it */
    inputKey?: number;
    onClose: () => void;
    onChange: (value: string) => void;
    onValidate: () => void;
    onSecondaryAction?: () => void;
    onEnterPress?: () => void;
};

function ScannerModal({
    open,
    title,
    label,
    placeholder,
    value,
    error,
    showInput = true,
    infoText,
    mappedValueText,
    primaryButtonText = 'Validate',
    secondaryButtonText,
    secondaryButtonStartIcon,
    inputKey,
    onClose,
    onChange,
    onValidate,
    onSecondaryAction,
    onEnterPress,
}: ScannerModalProps) {
    const classes = useCommonStyles();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: '16px',
                        minWidth: { xs: '92vw', sm: 520 },
                    },
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Typography component="div" className={classes.labelNoto20Bold600}>
                    {title}
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                {showInput ? (
                    <FormControl size="small" fullWidth>
                        <FormLabel sx={{ color: '#404040', mb: 1 }} className={classes.labelInter14Bold400}>
                            {label}
                        </FormLabel>
                        <ScanInput
                            key={inputKey}
                            softFocusOnMount
                            submitOnScan={false}
                            scanPickupEnabled={false}
                            defaultValue={value}
                            onChange={(val: string) => {
                                onChange(val);
                            }}
                            onEnterPress={onEnterPress || onValidate}
                            manualEdit
                            error={Boolean(error)}
                            errorText={error}
                            label={label}
                            placeholder={placeholder}
                        />
                        {mappedValueText ? (
                            <Typography sx={{ mt: 1 }} color="#4A5565" className={classes.labelNoto12Bold500}>
                                {mappedValueText}
                            </Typography>
                        ) : null}
                    </FormControl>
                ) : (
                    <Typography color="#4A5565" className={classes.labelNoto16Bold400}>
                        {infoText ?? 'No equipment configured for this step.'}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button variant="contained" disableRipple className={classes.whiteButton} onClick={onClose}>
                    Cancel
                </Button>
                {secondaryButtonText && onSecondaryAction ? (
                    <Button variant="contained" disableRipple className={classes.redBorderRedLabel} startIcon={secondaryButtonStartIcon ?? undefined} onClick={onSecondaryAction}>
                        {secondaryButtonText}
                    </Button>
                ) : null}
                <Button variant="contained" disableRipple className={classes.greenButton} onClick={onValidate}>
                    {primaryButtonText}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default memo(ScannerModal);
